import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useNotification } from 'shared-lib';
import { Container, Typography, Box, Button, CircularProgress } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { VendorRegistrationForm } from '../components/VendorRegistrationForm';
import { useQueryClient } from '@tanstack/react-query';

const Registration = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleRegistrationSuccess = async () => {
    setIsRedirecting(true);
    
    showNotification({
      message: 'Registration completed successfully!',
      severity: 'success',
      autoHideDuration: 3000
    });
    
    try {
      // Invalidate vendor queries to force a refetch
      await queryClient.invalidateQueries({ queryKey: ['vendors', currentUser?.uid] });
      await queryClient.invalidateQueries({ queryKey: ['hasVendorAccess', currentUser?.uid] });
      
      // Give time for the query cache to update and fetch new data
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Now navigate to the home page
      navigate('/', { state: { from: '/register' } });
    } catch (error) {
      console.error('Error refreshing vendor data:', error);
      setIsRedirecting(false);
      
      // Navigate anyway to prevent getting stuck
      navigate('/', { state: { from: '/register' } });
    }
  };

  const handleRegistrationError = (error: Error) => {
    console.error('Registration error:', error);
    showNotification({
      message: error.message,
      severity: 'error',
      autoHideDuration: 6000
    });
  };

  if (!currentUser) {
    showNotification({
      message: 'You must be logged in to register. Please log in first.',
      severity: 'warning',
      autoHideDuration: 6000
    });
    
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h4">Authentication Required</Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Please log in to continue with registration.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/login')}
            sx={{ mt: 3 }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Register your Vendor
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            Complete the form below to register your organization as a vendor in the Rent Ready Bids system.
          </Typography>
          
          {isRedirecting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Processing registration and preparing your dashboard...</Typography>
            </Box>
          ) : (
            <VendorRegistrationForm 
              onSuccess={handleRegistrationSuccess} 
              onError={handleRegistrationError} 
            />
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Registration; 